const yaml = require('js-yaml');

class WebDataService {
  constructor() {
    this.dataDirectoryHandle = null;
    this.configKey = 'skillsApp_config';
    this.dataKey = 'skillsApp_data';
    this.hasFileSystemAccess = 'showDirectoryPicker' in window;
  }

  // Check if File System Access API is supported
  supportsFileSystemAccess() {
    return this.hasFileSystemAccess;
  }

  // Initialize the service - either with File System Access or fallback
  async initialize() {
    try {
      // Try to restore previous directory handle
      const config = this.loadConfig();
      if (config && config.directoryHandle && this.hasFileSystemAccess) {
        // Verify we still have permission to the directory
        try {
          this.dataDirectoryHandle = config.directoryHandle;
          await this.dataDirectoryHandle.requestPermission({ mode: 'readwrite' });
          return this.dataDirectoryHandle.name;
        } catch (error) {
          console.log('Previous directory no longer accessible:', error);
          this.dataDirectoryHandle = null;
        }
      }

      // If no previous directory or File System Access not available, use fallback
      if (!this.hasFileSystemAccess) {
        return this.initializeFallbackMode();
      }

      return null; // User needs to select directory
    } catch (error) {
      console.error('Failed to initialize:', error);
      return null;
    }
  }

  // Initialize fallback mode with localStorage
  initializeFallbackMode() {
    const data = localStorage.getItem(this.dataKey);
    if (data) {
      return 'Browser Storage';
    }
    return null;
  }

  // Select data directory (File System Access API)
  async selectDataFolder() {
    if (!this.hasFileSystemAccess) {
      throw new Error('File System Access API not supported. Use fallback methods.');
    }

    try {
      this.dataDirectoryHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'documents'
      });

      await this.ensureDataStructure();
      this.saveConfig({ directoryHandle: this.dataDirectoryHandle });
      return this.dataDirectoryHandle.name;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null; // User cancelled
      }
      throw error;
    }
  }

  // Ensure required files exist in the directory
  async ensureDataStructure() {
    if (!this.dataDirectoryHandle) return;

    try {
      // Create people directory if it doesn't exist
      await this.dataDirectoryHandle.getDirectoryHandle('people', { create: true });

      // Create capabilities.yaml if it doesn't exist
      try {
        await this.dataDirectoryHandle.getFileHandle('capabilities.yaml');
      } catch {
        const defaultCapabilities = { capabilities: [] };
        await this.writeFileToDirectory('capabilities.yaml', yaml.dump(defaultCapabilities));
      }

      // Create skills.yaml if it doesn't exist
      try {
        await this.dataDirectoryHandle.getFileHandle('skills.yaml');
      } catch {
        const defaultSkills = { skills: [], categories: [] };
        await this.writeFileToDirectory('skills.yaml', yaml.dump(defaultSkills));
      }
    } catch (error) {
      console.error('Error ensuring data structure:', error);
    }
  }

  // Write file to directory (File System Access API)
  async writeFileToDirectory(fileName, content) {
    const fileHandle = await this.dataDirectoryHandle.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(content);
    await writable.close();
  }

  // Read file from directory (File System Access API)
  async readFileFromDirectory(fileName) {
    const fileHandle = await this.dataDirectoryHandle.getFileHandle(fileName);
    const file = await fileHandle.getFile();
    return await file.text();
  }

  // List YAML files in people directory
  async listPeopleFiles() {
    const peopleDir = await this.dataDirectoryHandle.getDirectoryHandle('people');
    const files = [];
    
    for await (const [name, handle] of peopleDir.entries()) {
      if (handle.kind === 'file' && name.endsWith('.yaml')) {
        files.push(name);
      }
    }
    
    return files;
  }

  // Load capabilities from File System or fallback
  async loadCapabilities() {
    try {
      if (this.hasFileSystemAccess && this.dataDirectoryHandle) {
        const content = await this.readFileFromDirectory('capabilities.yaml');
        const data = yaml.load(content);
        return data.capabilities || [];
      } else {
        return this.loadCapabilitiesFromStorage();
      }
    } catch (error) {
      console.error('Error loading capabilities:', error);
      return [];
    }
  }

  // Save capabilities to File System or fallback
  async saveCapabilities(capabilities) {
    try {
      if (this.hasFileSystemAccess && this.dataDirectoryHandle) {
        const data = { capabilities };
        const yamlContent = yaml.dump(data);
        await this.writeFileToDirectory('capabilities.yaml', yamlContent);
      } else {
        await this.saveCapabilitiesToStorage(capabilities);
      }
    } catch (error) {
      console.error('Error saving capabilities:', error);
      throw error;
    }
  }

  // Load skills from File System or fallback
  async loadSkills() {
    try {
      if (this.hasFileSystemAccess && this.dataDirectoryHandle) {
        const content = await this.readFileFromDirectory('skills.yaml');
        const data = yaml.load(content);
        return {
          skills: data.skills || [],
          categories: data.categories || []
        };
      } else {
        return this.loadSkillsFromStorage();
      }
    } catch (error) {
      console.error('Error loading skills:', error);
      return { skills: [], categories: [] };
    }
  }

  // Save skills to File System or fallback
  async saveSkills(skills, categories) {
    try {
      if (this.hasFileSystemAccess && this.dataDirectoryHandle) {
        const data = { skills, categories };
        const yamlContent = yaml.dump(data);
        await this.writeFileToDirectory('skills.yaml', yamlContent);
      } else {
        await this.saveSkillsToStorage(skills, categories);
      }
    } catch (error) {
      console.error('Error saving skills:', error);
      throw error;
    }
  }

  // Load people from File System or fallback
  async loadPeople() {
    try {
      if (this.hasFileSystemAccess && this.dataDirectoryHandle) {
        const files = await this.listPeopleFiles();
        const people = [];

        for (const fileName of files) {
          try {
            const peopleDir = await this.dataDirectoryHandle.getDirectoryHandle('people');
            const fileHandle = await peopleDir.getFileHandle(fileName);
            const file = await fileHandle.getFile();
            const content = await file.text();
            const data = yaml.load(content);
            if (data.person) {
              people.push(data.person);
            }
          } catch (error) {
            console.error(`Error loading person file ${fileName}:`, error);
          }
        }

        return people;
      } else {
        return this.loadPeopleFromStorage();
      }
    } catch (error) {
      console.error('Error loading people:', error);
      return [];
    }
  }

  // Save person to File System or fallback
  async savePerson(person) {
    try {
      if (this.hasFileSystemAccess && this.dataDirectoryHandle) {
        const fileName = `${person.name.replace(/[^a-zA-Z0-9]/g, '_')}.yaml`;
        const data = { person };
        const yamlContent = yaml.dump(data);
        
        const peopleDir = await this.dataDirectoryHandle.getDirectoryHandle('people');
        const fileHandle = await peopleDir.getFileHandle(fileName, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(yamlContent);
        await writable.close();
      } else {
        await this.savePersonToStorage(person);
      }
    } catch (error) {
      console.error('Error saving person:', error);
      throw error;
    }
  }

  // Delete person from File System or fallback
  async deletePerson(personName) {
    try {
      if (this.hasFileSystemAccess && this.dataDirectoryHandle) {
        const fileName = `${personName.replace(/[^a-zA-Z0-9]/g, '_')}.yaml`;
        const peopleDir = await this.dataDirectoryHandle.getDirectoryHandle('people');
        await peopleDir.removeEntry(fileName);
      } else {
        await this.deletePersonFromStorage(personName);
      }
    } catch (error) {
      console.error('Error deleting person:', error);
      throw error;
    }
  }

  // Get data path name
  getDataPath() {
    if (this.hasFileSystemAccess && this.dataDirectoryHandle) {
      return this.dataDirectoryHandle.name;
    }
    return 'Browser Storage';
  }

  // Change data path
  async changeDataPath() {
    return await this.selectDataFolder();
  }

  // === FALLBACK METHODS FOR BROWSERS WITHOUT FILE SYSTEM ACCESS ===

  // Load/save data from localStorage
  loadDataFromStorage() {
    try {
      const data = localStorage.getItem(this.dataKey);
      return data ? JSON.parse(data) : { capabilities: [], skills: [], categories: [], people: [] };
    } catch (error) {
      console.error('Error loading data from storage:', error);
      return { capabilities: [], skills: [], categories: [], people: [] };
    }
  }

  saveDataToStorage(data) {
    try {
      localStorage.setItem(this.dataKey, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving data to storage:', error);
      throw error;
    }
  }

  loadCapabilitiesFromStorage() {
    const data = this.loadDataFromStorage();
    return data.capabilities || [];
  }

  async saveCapabilitiesToStorage(capabilities) {
    const data = this.loadDataFromStorage();
    data.capabilities = capabilities;
    this.saveDataToStorage(data);
  }

  loadSkillsFromStorage() {
    const data = this.loadDataFromStorage();
    return {
      skills: data.skills || [],
      categories: data.categories || []
    };
  }

  async saveSkillsToStorage(skills, categories) {
    const data = this.loadDataFromStorage();
    data.skills = skills;
    data.categories = categories;
    this.saveDataToStorage(data);
  }

  loadPeopleFromStorage() {
    const data = this.loadDataFromStorage();
    return data.people || [];
  }

  async savePersonToStorage(person) {
    const data = this.loadDataFromStorage();
    const people = data.people || [];
    const existingIndex = people.findIndex(p => p.name === person.name);
    
    if (existingIndex >= 0) {
      people[existingIndex] = person;
    } else {
      people.push(person);
    }
    
    data.people = people;
    this.saveDataToStorage(data);
  }

  async deletePersonFromStorage(personName) {
    const data = this.loadDataFromStorage();
    data.people = (data.people || []).filter(p => p.name !== personName);
    this.saveDataToStorage(data);
  }

  // === FALLBACK FILE IMPORT/EXPORT METHODS ===

  // Export all data as ZIP file
  async exportData() {
    try {
      const [capabilities, skillsData, people] = await Promise.all([
        this.loadCapabilities(),
        this.loadSkills(),
        this.loadPeople()
      ]);

      const files = {
        'capabilities.yaml': yaml.dump({ capabilities }),
        'skills.yaml': yaml.dump({ skills: skillsData.skills, categories: skillsData.categories }),
      };

      // Add people files
      people.forEach(person => {
        const fileName = `people/${person.name.replace(/[^a-zA-Z0-9]/g, '_')}.yaml`;
        files[fileName] = yaml.dump({ person });
      });

      return files;
    } catch (error) {
      console.error('Error exporting data:', error);
      throw error;
    }
  }

  // Import data from files
  async importData(files) {
    try {
      for (const [fileName, content] of Object.entries(files)) {
        const data = yaml.load(content);
        
        if (fileName === 'capabilities.yaml' && data.capabilities) {
          await this.saveCapabilities(data.capabilities);
        } else if (fileName === 'skills.yaml') {
          await this.saveSkills(data.skills || [], data.categories || []);
        } else if (fileName.startsWith('people/') && data.person) {
          await this.savePerson(data.person);
        }
      }
    } catch (error) {
      console.error('Error importing data:', error);
      throw error;
    }
  }

  // Config management
  loadConfig() {
    try {
      const config = localStorage.getItem(this.configKey);
      return config ? JSON.parse(config) : null;
    } catch (error) {
      return null;
    }
  }

  saveConfig(config) {
    try {
      localStorage.setItem(this.configKey, JSON.stringify(config));
    } catch (error) {
      console.error('Error saving config:', error);
    }
  }
}

export default WebDataService;